/* eslint-disable import/prefer-default-export */
import { createRef, useEffect, useState } from 'react';
import memoizeOne from 'memoize-one';

import { getLmsLink } from '@utils/edxLinks';
import { addTrackingToLink } from '@utils/analytics';

/**
 * This hook contains the state and functions necessary
 * to manage the subscription status,
 * handle user interactions with the button,
 * and communicate with the backend to update the subscription.
 */

export const useSubscriptionButton = (props) => {
  const [hasEnrollment, setHasEnrollment] = useState(false);
  const [hasSkus, setHasSkus] = useState(false);
  const [outstandingCourses, setOutstandingCourses] = useState(false);

  const linkRef = createRef();

  const isEntitledToCourse = (uuid) => {
    const { userEntitlements } = props;

    return userEntitlements.some(entitlement => entitlement.courseUuid === uuid && entitlement.expirationDate === null);
  };

  const isEnrolledInCourseWithCertificate = (courseRunKeys = []) => {
    const { userEnrollments, applicableSeatTypes: seatTypes } = props;

    return userEnrollments.some((enrollment) => {
      // is enrolled in the course
      const isEnrolled = courseRunKeys.some(key => enrollment.courseId === key);
      /**
             * Check if any applicableSeatTypes are a substring of an entitlement mode
             * necessary for the no-id-professional case, which should count as an applicableSeatType
             */
      const isApplicableSeatType = seatTypes.some(seatType => enrollment.mode.includes(seatType));

      return isEnrolled && isApplicableSeatType;
    });
  };

  const userIsInCourseWithCertificate = (course) => {
    const { courseRunKeys, uuid } = course;

    return isEnrolledInCourseWithCertificate(courseRunKeys)
      || isEntitledToCourse(uuid);
  };

  const {
    id,
    userLoggedIn,
    uuid,
  } = props;

  /**
     * Retrieve a seat with applicable seat type from the course run seats.
     */
  const getApplicableSeat = (seats = []) => {
    const { applicableSeatTypes: seatTypes } = props;
    return seats.find(seat => seatTypes.includes(seat.type));
  };

  /**
   * Retrieve an entitlement with applicable type from the course entitlements.
   */
  const getApplicableEntitlement = (entitlements = []) => {
    const { applicableSeatTypes: seatTypes } = props;
    return entitlements.find(entitlement => (entitlement.expires == null || entitlement.expires > Date.now())
      && seatTypes.includes(entitlement.mode));
  };

  const getProductForCourse = (course) => {
    const entitlement = getApplicableEntitlement(course.entitlements);
    const { activeCourseRun } = course;
    const seat = activeCourseRun ? getApplicableSeat(activeCourseRun.seats) : null;
    return entitlement || seat;
  };

  const getSkuQueryStringForPartialBundle = () => outstandingCourses
    .reduce((queryString, courseNode) => {
      const entitlement = getProductForCourse(courseNode);
      if (entitlement?.sku) {
        const skuParam = `sku=${encodeURIComponent(entitlement.sku)}`;
        if (queryString === '') {
          return skuParam;
        }
        return `${queryString}&${skuParam}`;
      }
      return queryString;
    }, '');

  const getSkuQueryStringForFullBundle = () => {
    const { skus } = props;
    if (!skus || skus.length === 0) {
      return '';
    }
    return skus.reduce((queryString, sku, index) => {
      const skuParam = `sku=${sku}`;
      if (index === 0) {
        return skuParam;
      }
      return `${queryString}&${skuParam}`;
    }, '');
  };

  /**
     * Get the courses in a program that the user has not enrolled in with a
     * certificate and does not have an entitlement for.
     * @returns {*}
     */
  const getOutstandingCourses = () => {
    const { courses } = props;
    let hasCourseSku = false;
    let isUserEnrolled = false;

    const remainingCourses = courses
      //  reject courses user is enrolled in with a certificate
      .filter((courseNode) => {
        const userHasEnrollment = userIsInCourseWithCertificate(courseNode);

        if (userHasEnrollment) {
          isUserEnrolled = true;
        }

        return !userHasEnrollment;
      })
      //  reject courses user has entitlements for
      .filter((courseNode) => {
        const sku = getProductForCourse(courseNode);
        const skuIsUndefined = sku === undefined;

        if (!skuIsUndefined) {
          hasCourseSku = true;
        }

        return !skuIsUndefined;
      });

    if ((hasCourseSku && hasSkus !== hasCourseSku)
      || (hasEnrollment && isUserEnrolled !== hasEnrollment)) {
      setHasSkus(hasCourseSku);
      setHasEnrollment(isUserEnrolled);
    }

    return remainingCourses;
  };

  const setUserOutstandingCourses = (userEnrollments, userEntitlements) => {
    // Is user has no enrollment or entitlements return program course count
    const { courses } = props;

    const latestOutstandingCourses = ((userEnrollments.length + userEntitlements.length) === 0)
      ? courses : getOutstandingCourses();

    if (outstandingCourses !== latestOutstandingCourses) {
      setOutstandingCourses(latestOutstandingCourses);
    }
  };
  // memoized methods that are only called once unless the passed in params change
  const memoizedSetOutstandingCourses = memoizeOne(setUserOutstandingCourses);

  const getLinkProps = () => {
    const { queryID, index, position } = props.search;
    return {
      category: 'navigation',
      link: linkRef.current.getAttribute('href'),
      index,
      queryID,
      position,
      objectID: `program-${props.uuid}`,
      // Algolia requires the objectID to be in a 'products' property as well as top-level
      products: [{ objectID: `program-${props.uuid}` }],
    };
  };

  useEffect(() => {
    memoizedSetOutstandingCourses(props.userEnrollments, props.userEntitlements);
  }, [props.userEnrollments, props.userEntitlements]);

  useEffect(() => {
    const {
      displayTrackModal,
      userEnrollments,
      userEntitlements,
      tracking,
    } = props;

    memoizedSetOutstandingCourses(userEnrollments, userEntitlements);
    if (!displayTrackModal) {
      addTrackingToLink(tracking, 'edx.bi.link.pursue-program.clicked', linkRef.current, getLinkProps);
    }
  }, [props.displayTrackModal]);

  const handlePursueProgramClick = (event) => {
    const {
      displayTrackModal,
    } = props;

    if (displayTrackModal) {
      event.preventDefault();
      return false;
    }
    return true;
  };

  const getEnrollmentUrl = () => {
    if (props.displayTrackModal) {
      return '';
    }

    const skuQueryString = hasEnrollment || hasSkus
      ? getSkuQueryStringForPartialBundle()
      : getSkuQueryStringForFullBundle();

    const baseURL = process.env.GATSBY_SUBSCRIPTIONS_DOMAIN + process.env.GATSBY_STRIPE_SUBSCRIBE_API_PATH;

    return `${baseURL}${uuid}/?${skuQueryString}`;
  };

  const outstandingEnrollmentCount = outstandingCourses.length;
  const userFullyEnrolled = userLoggedIn && outstandingEnrollmentCount === 0;

  const linkUrl = userFullyEnrolled ? getLmsLink(`/dashboard/programs/${uuid}`) : getEnrollmentUrl();
  const hasId = !!id;

  return {
    linkUrl,
    hasId,
    userFullyEnrolled,
    outstandingEnrollmentCount,
    handlePursueProgramClick,
    userLoggedIn,
    hasEnrollment,
    hasSkus,
    outstandingCourses,
    ...props,
  };
};
